import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';

/**
 * Reusable DataTable component with search, sorting, and pagination.
 *
 * Props:
 *   columns       – Array of { key, label, sortable?, render?, headerClassName?, className? }
 *   data          – Array of row objects
 *   defaultSort   – { key, direction } (optional)
 *   pageSize      – Number of rows per page (default 10)
 *   searchable    – Enable the built-in search bar (default true)
 *   searchPlaceholder – Placeholder text for search (default "Search…")
 *   searchKeys    – Array of keys to search against (default: all column keys)
 *   emptyMessage  – String or JSX shown when no data matches
 *   emptyIcon     – Optional icon component for empty state
 *   className     – Extra wrapper classes
 *   onRowClick    – Optional (row) => void callback when a row is clicked
 *   rowKey        – Function or string to derive unique key per row (default 'id')
 *   stickyHeader  – Keep header visible when scrolling (default false)
 *   pageSizeOptions – Array of page size options (default [10, 25, 50])
 *   headerRight   – Optional JSX rendered to the right of the search bar
 */
const DataTable = ({
  columns = [],
  data = [],
  defaultSort = null,
  pageSize: initialPageSize = 10,
  searchable = true,
  searchPlaceholder = 'Search…',
  searchKeys,
  emptyMessage = 'No results found.',
  emptyIcon: EmptyIcon,
  className = '',
  onRowClick,
  rowKey = 'id',
  stickyHeader = false,
  pageSizeOptions = [10, 25, 50],
  headerRight,
}) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort || null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // ── Determine which keys to search ──────────────────────────────────
  const effectiveSearchKeys = useMemo(
    () => searchKeys || columns.map((c) => c.key),
    [searchKeys, columns]
  );

  // ── Filter ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      effectiveSearchKeys.some((key) => {
        const val = row[key];
        if (val == null) return false;
        return String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, effectiveSearchKeys]);

  // ── Sort ────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, direction } = sort;
    const col = columns.find((c) => c.key === key);
    return [...filtered].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      // Use custom sort value extractor if the column provides a render
      if (col?.sortValue) {
        aVal = col.sortValue(a);
        bVal = col.sortValue(b);
      }
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  // ── Paginate ────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Reset to first page when data changes
  const safePage = Math.min(page, totalPages - 1);
  const paged = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);

  // ── Handlers ────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSort((prev) => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        return null; // third click removes sort
      }
      return { key, direction: 'asc' };
    });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  const getRowKey = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row, index);
    return row[rowKey] ?? index;
  };

  // ── Render sort indicator ───────────────────────────────────────────
  const SortIndicator = ({ colKey }) => {
    if (sort?.key !== colKey) {
      return (
        <span className="ml-1 inline-flex flex-col text-gray-300 dark:text-gray-600">
          <ChevronUpIcon className="h-3 w-3 -mb-0.5" />
          <ChevronDownIcon className="h-3 w-3 -mt-0.5" />
        </span>
      );
    }
    return sort.direction === 'asc' ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 inline text-primary-500" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 inline text-primary-500" />
    );
  };

  // ── Pagination range helper ─────────────────────────────────────────
  const startRow = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const endRow = Math.min((safePage + 1) * pageSize, sorted.length);

  return (
    <div className={`bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg overflow-hidden ${className}`}>
      {/* ── Toolbar: Search + optional right content ───────────────── */}
      {(searchable || headerRight) && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {searchable && (
            <div className="relative w-full sm:max-w-xs">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white placeholder-gray-400"
              />
            </div>
          )}
          {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className={`bg-gray-50 dark:bg-gray-700 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${
                    col.sortable !== false ? 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100' : ''
                  } ${col.headerClassName || ''}`}
                >
                  <span className="inline-flex items-center">
                    {col.label}
                    {col.sortable !== false && <SortIndicator colKey={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  {EmptyIcon && (
                    <EmptyIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
                </td>
              </tr>
            ) : (
              paged.map((row, idx) => (
                <tr
                  key={getRowKey(row, idx)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 py-4 whitespace-nowrap text-sm ${col.className || 'text-gray-500 dark:text-gray-300'}`}
                    >
                      {col.render ? col.render(row) : row[col.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination footer ──────────────────────────────────────── */}
      {sorted.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500 dark:text-gray-400">
          {/* Left: rows per page */}
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Center: showing X–Y of Z */}
          <span>
            Showing {startRow}–{endRow} of {sorted.length}
            {search && sorted.length !== data.length && (
              <span className="text-gray-400 dark:text-gray-500"> (filtered from {data.length})</span>
            )}
          </span>

          {/* Right: page controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={safePage === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="First page"
            >
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="px-2 font-medium text-gray-700 dark:text-gray-300">
              {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={safePage >= totalPages - 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Last page"
            >
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
